import { useCallback, useEffect, useRef, useState } from 'react'

import { RequestStatus } from '../../domain/Request'
import { __PROD__ } from '../../infra/environment'

const DEBUG = !__PROD__

export type AnyFunction = (...args: any) => any

export type Result<Fn extends AnyFunction> = Awaited<ReturnType<Fn>>

export type Caller<Fn extends AnyFunction> = (...args: Parameters<Fn>) => void

export type Return<Fn extends AnyFunction> = {
  call: Caller<Fn>
  reset: () => void
  result?: Result<Fn>
  error?: unknown
  status: RequestStatus
  isInitial: boolean
  isPending: boolean
  isDone: boolean
  hasError: boolean
}

type State<Fn extends AnyFunction> = {
  result?: Result<Fn>
  error?: unknown
  status: RequestStatus
}

function getInitialState<Fn extends AnyFunction>(): State<Fn> {
  return { result: undefined, error: undefined, status: RequestStatus.Initial }
}

/**
 * Hook that manages the RequestStatus.
 *
 * @param fn Could be either an asynchronous function, a function that returns a
 * Promise or a synchronous function.
 */
export function useAsync<Fn extends AnyFunction>(fn: Fn): Return<Fn> {
  const [{ result, error, status }, setState] = useState(getInitialState<Fn>)
  const isMounted = useRef(true)

  const call = useCallback<Caller<Fn>>(
    (...args) => {
      if (isMounted.current) {
        setState({ ...getInitialState<Fn>(), status: RequestStatus.Pending })

        Promise.resolve()
          .then(() => fn(...args))
          .then(newResult => {
            if (isMounted.current) {
              setState(prevState => ({
                ...prevState,
                status: RequestStatus.Done,
                result: newResult,
              }))
            } else if (DEBUG) {
              console.debug("useRequest's call resolved, but the component is unmounted")
            }
          })
          .catch(newError => {
            if (isMounted.current) {
              setState(prevState => ({
                ...prevState,
                status: RequestStatus.Error,
                error: newError,
              }))
            } else if (DEBUG) {
              console.debug("useRequest's call rejected, but the component is unmounted")
            }
          })
      } else if (DEBUG) {
        console.debug("useRequest's call called, but the component is unmounted")
      }
    },
    [fn]
  )

  const reset = useCallback(() => {
    setState(getInitialState<Fn>())
  }, [])

  useEffect(() => {
    reset()
  }, [reset, fn])

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const isInitial = status === RequestStatus.Initial
  const isPending = status === RequestStatus.Pending
  const isDone = status === RequestStatus.Done
  const hasError = status === RequestStatus.Error

  return { call, reset, result, error, status, isInitial, isPending, isDone, hasError }
}
